import { useState } from 'react'

/**
 * CombinedScoreBadge - Displays the combined match score with breakdown tooltip
 * 
 * Score Formula: (Solo Confidence × 40%) + (Schedule Fit × 40%) + (Distance × 20%)
 * 
 * Color coding:
 * - Green (80+): Excellent match
 * - Amber (60-79): Good match
 * - Gray (50-59): Fair match
 */
export default function CombinedScoreBadge({
  combinedScore,
  soloConfidence,
  scheduleFit,
  distanceScore,
  distanceMiles,
  matchReason,
  compact = false
}) {
  const [showTooltip, setShowTooltip] = useState(false)

  if (combinedScore === null || combinedScore === undefined) return null

  // Determine color based on score
  const getScoreConfig = (score) => {
    if (score >= 80) {
      return {
        bgClass: 'bg-green-100',
        textClass: 'text-green-800',
        borderClass: 'border-green-300',
        ringClass: 'ring-green-500',
        label: 'Excellent Match',
        icon: '🟢'
      }
    }
    if (score >= 60) {
      return {
        bgClass: 'bg-amber-100',
        textClass: 'text-amber-800',
        borderClass: 'border-amber-300',
        ringClass: 'ring-amber-500',
        label: 'Good Match',
        icon: '🟡'
      }
    }
    return {
      bgClass: 'bg-gray-100',
      textClass: 'text-gray-700',
      borderClass: 'border-gray-300',
      ringClass: 'ring-gray-500',
      label: 'Fair Match',
      icon: '⚪'
    }
  }

  const config = getScoreConfig(combinedScore)

  // Calculate component contributions for tooltip
  const soloContribution = soloConfidence !== undefined ? Math.round(soloConfidence * 0.4) : null
  const scheduleContribution = scheduleFit !== undefined ? Math.round(scheduleFit * 0.4) : null
  const distanceContribution = distanceScore !== undefined ? Math.round(distanceScore * 0.2) : null

  const renderTooltip = () => (
    <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-white rounded-lg shadow-lg border border-gray-200 text-sm">
      <div className="font-semibold text-gray-900 mb-2">{config.label}</div>
      
      <div className="space-y-2">
        {/* Solo Confidence */}
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Solo Ice Confidence</span>
          <span className="font-medium">
            {soloConfidence !== undefined ? (
              <>{soloConfidence}% <span className="text-gray-400">× 40%</span> = {soloContribution}%</>
            ) : '—'}
          </span>
        </div>
        
        {/* Schedule Fit */}
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Schedule Fit</span>
          <span className="font-medium">
            {scheduleFit !== undefined ? (
              <>{scheduleFit}% <span className="text-gray-400">× 40%</span> = {scheduleContribution}%</>
            ) : '—'}
          </span>
        </div>
        
        {/* Distance */}
        <div className="flex items-center justify-between">
          <span className="text-gray-600">
            Distance
            {distanceMiles !== undefined && distanceMiles !== null && (
              <span className="text-gray-400 ml-1">({Math.round(distanceMiles)} mi)</span>
            )}
          </span>
          <span className="font-medium">
            {distanceScore !== undefined ? (
              <>{distanceScore}% <span className="text-gray-400">× 20%</span> = {distanceContribution}%</>
            ) : '—'}
          </span>
        </div>
        
        {/* Divider */}
        <div className="border-t border-gray-200 pt-2 mt-2">
          <div className="flex items-center justify-between font-semibold">
            <span className="text-gray-700">Combined Score</span>
            <span className={config.textClass}>{combinedScore}%</span>
          </div>
        </div>
      </div>
      
      {/* Arrow */}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
        <div className="border-8 border-transparent border-t-white"></div>
      </div>
    </div>
  )

  if (compact) {
    return (
      <div 
        className="relative inline-flex"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${config.bgClass} ${config.textClass} cursor-help`}>
          {combinedScore}%
        </span>
        {showTooltip && renderTooltip()}
      </div>
    )
  }

  return (
    <div 
      className={`relative rounded-lg border px-3 py-2.5 ${config.bgClass} ${config.borderClass} cursor-help`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{config.icon}</span>
          <div>
            <div className={`font-semibold text-sm ${config.textClass}`}>
              {config.label}
            </div>
            <div className="text-xs opacity-80">
              {matchReason ? (
                <>{matchReason}</>
              ) : distanceMiles !== undefined && distanceMiles !== null ? (
                <>{Math.round(distanceMiles)} miles away</>
              ) : (
                <>Hover for score breakdown</>
              )}
            </div>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-sm font-bold ${config.bgClass} ${config.textClass} border ${config.borderClass}`}>
          {combinedScore}%
        </div>
      </div>
      {showTooltip && renderTooltip()}
    </div>
  )
}
